import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Link2,
  Copy,
  Trash2,
  ExternalLink,
  Plus,
  TrendingUp,
  Bitcoin,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LinksPage = () => {
  const { refreshUser } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newTitle, setNewTitle] = useState('');

  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/links`, { withCredentials: true });
      setLinks(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch links:', error);
      }
      toast.error('Failed to load links');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const linkStats = useMemo(() => {
    const totalClicks = links.reduce((sum, l) => sum + l.clicks, 0);
    const totalEarnings = links.reduce((sum, l) => sum + l.earnings_satoshis, 0);
    const avgPerClick = totalClicks > 0 ? Math.round(totalEarnings / totalClicks) : 0;
    return { totalClicks, totalEarnings, avgPerClick };
  }, [links]);

  const createLink = async (e) => {
    e.preventDefault();
    if (!newUrl) return;

    setCreating(true);
    try {
      const response = await axios.post(
        `${API}/links`,
        { original_url: newUrl, title: newTitle || null },
        { withCredentials: true }
      );
      setLinks([response.data, ...links]);
      setNewUrl('');
      setNewTitle('');
      setDialogOpen(false);
      toast.success('Link created successfully!');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const deleteLink = async (linkId) => {
    try {
      await axios.delete(`${API}/links/${linkId}`, { withCredentials: true });
      setLinks(links.filter((l) => l.id !== linkId));
      toast.success('Link deleted');
      await refreshUser();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const copyLink = async (shortCode) => {
    const link = `${window.location.origin}/r/${shortCode}`;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Link copied to clipboard!');
    } catch (err) {
      toast.error('Could not copy - please copy the link manually');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading links...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
              My <span className="text-gradient-gold">Links</span>
            </h1>
            <p className="text-slate-400">
              Create shortened links and earn satoshis for every click
            </p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-gold" data-testid="create-link-btn">
                <Plus className="w-5 h-5 mr-2" />
                Create Link
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#131622] border-white/10">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Create New Link</DialogTitle>
              </DialogHeader>
              <form onSubmit={createLink} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="url" className="text-slate-300">
                    URL to Shorten *
                  </Label>
                  <Input
                    id="url"
                    type="url"
                    placeholder="https://example.com/your-long-url"
                    value={newUrl}
                    onChange={(e) => setNewUrl(e.target.value)}
                    className="bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                    required
                    data-testid="url-shortener-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title" className="text-slate-300">
                    Title (Optional)
                  </Label>
                  <Input
                    id="title"
                    type="text"
                    placeholder="My awesome link"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    className="bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full btn-gold py-5"
                  disabled={creating}
                  data-testid="url-shortener-submit-btn"
                >
                  {creating ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Short Link
                      <Link2 className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold font-mono">{links.length}</p>
            <p className="text-sm text-slate-400">Total Links</p>
          </div>
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold font-mono text-cyan-400">
              {linkStats.totalClicks.toLocaleString()}
            </p>
            <p className="text-sm text-slate-400">Total Clicks</p>
          </div>
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold font-mono text-[#F7931A]">
              {linkStats.totalEarnings.toLocaleString()}
            </p>
            <p className="text-sm text-slate-400">Satoshis Earned</p>
          </div>
          <div className="card-dark p-4 text-center">
            <p className="text-2xl font-bold font-mono text-emerald-400">
              {linkStats.avgPerClick}
            </p>
            <p className="text-sm text-slate-400">Avg. per Click</p>
          </div>
        </div>

        {/* Links List */}
        {links.length > 0 ? (
          <div className="space-y-4">
            {links.map((link) => (
              <div key={link.id} className="card-dark p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Link2 className="w-5 h-5 text-[#F7931A] flex-shrink-0" />
                      <h3 className="font-semibold truncate">{link.title}</h3>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className="font-mono text-[#F7931A] bg-[#F7931A]/10 px-2 py-1 rounded"
                          data-testid="shortened-link-output"
                        >
                          {`${window.location.origin}/r/${link.short_code}`}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyLink(link.short_code)}
                          className="p-1 h-auto hover:bg-white/5"
                          data-testid="copy-link-btn"
                        >
                          <Copy className="w-4 h-4 text-slate-400" />
                        </Button>
                      </div>

                      <span className="text-slate-500 truncate hidden sm:inline">
                        → {link.original_url}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-cyan-400">
                        <TrendingUp className="w-4 h-4" />
                        <span className="font-mono font-bold">{link.clicks}</span>
                      </div>
                      <p className="text-xs text-slate-500">Clicks</p>
                    </div>

                    <div className="text-center">
                      <div className="flex items-center gap-1 text-[#F7931A]">
                        <Bitcoin className="w-4 h-4" />
                        <span className="font-mono font-bold">{link.earnings_satoshis}</span>
                      </div>
                      <p className="text-xs text-slate-500">Sats</p>
                    </div>

                    <div className="flex items-center gap-2">
                      <a
                        href={link.original_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 text-slate-400" />
                      </a>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteLink(link.id)}
                        className="p-2 h-auto hover:bg-red-500/10 hover:text-red-400"
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-dark p-12 text-center">
            <Link2 className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No Links Yet</h3>
            <p className="text-slate-400 mb-6">
              Create your first shortened link and start earning Bitcoin!
            </p>
            <Button className="btn-gold" onClick={() => setDialogOpen(true)}>
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Link
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinksPage;
